import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDeleteRun, useRuns } from "@/api/runs";
import type { Run, RunBackendState } from "@/api/types";
import { relativeTime } from "@/lib/relativeTime";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";
import { RunRowActions } from "./RunRowActions";
import { RunStatusBadge } from "./RunStatusBadge";

const KIND_LABEL: Record<string, string> = {
  datadog: "DD",
  aws: "AWS",
  azure: "AZ",
  dynatrace: "DT",
  grafana: "GR",
  splunk: "SP",
  adx: "AX",
  solarwinds: "SW",
  unknown: "?",
};

function BackendChips({
  backends,
  runStatus,
}: {
  backends: RunBackendState[] | undefined;
  runStatus: Run["status"];
}) {
  if (!backends || backends.length === 0) return null;
  const visible = backends.slice(0, 3);
  const overflow = backends.length - visible.length;
  // Legacy / upgraded runs synthesize `backends` via the load-time shim
  // but have no per-backend `push_result`. Treat those as "ok" when the
  // run itself is idle — showing pending chips forever misrepresents a
  // completed run.
  const runIsTerminalOk = runStatus === "idle";
  return (
    <div className="flex items-center gap-1">
      {visible.map((b) => {
        const failed = b.error !== null;
        const pushErr = b.push_result?.error ?? null;
        const partial = !failed && b.push_result !== null && pushErr !== null;
        const hasResult = b.push_result !== null;
        const legacyOk = !failed && !partial && !hasResult && runIsTerminalOk;
        const ok = (!failed && !partial && hasResult) || legacyOk;
        const tone = ok
          ? "border-green-500/40 bg-green-500/[0.08] text-green-700 dark:text-green-300"
          : failed
            ? "border-red-500/40 bg-red-500/[0.08] text-red-700 dark:text-red-300"
            : partial
              ? "border-amber-500/40 bg-amber-500/[0.08] text-amber-700 dark:text-amber-300"
              : "border-border bg-muted/40 text-muted-foreground";
        return (
          <span
            key={b.backend_id}
            className={cn(
              "inline-flex items-center h-5 px-1.5 rounded text-[10px] font-mono border",
              tone,
            )}
            title={`${b.backend_kind} · ${b.backend_id}`}
          >
            {KIND_LABEL[b.backend_kind] ?? b.backend_kind.slice(0, 2)
              .toUpperCase()}
          </span>
        );
      })}
      {overflow > 0 && (
        <span className="text-[10px] font-mono text-muted-foreground">
          +{overflow}
        </span>
      )}
    </div>
  );
}

function isDeletable(r: Run): boolean {
  // Mirrors backend rule: RUNNING / PENDING must be stopped before delete.
  return r.status !== "running" && r.status !== "pending";
}

export function RunsListPage() {
  const navigate = useNavigate();
  const runs = useRuns();
  const rows = runs.data ?? [];
  const sorted = useMemo(
    () => [...rows].sort((a, b) => b.created_at.localeCompare(a.created_at)),
    [rows],
  );

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const del = useDeleteRun();
  const permissions = usePermissions();

  // Drop selections that no longer exist (post-delete row removal).
  const visibleIds = useMemo(() => new Set(sorted.map((r) => r.id)), [sorted]);
  const liveSelection = useMemo(
    () => new Set([...selected].filter((id) => visibleIds.has(id))),
    [selected, visibleIds],
  );

  const deletableSorted = sorted.filter(isDeletable);
  const allDeletableSelected =
    deletableSorted.length > 0 &&
    deletableSorted.every((r) => liveSelection.has(r.id));
  const someSelected = liveSelection.size > 0 && !allDeletableSelected;

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected((prev) => {
      if (allDeletableSelected) {
        const next = new Set(prev);
        deletableSorted.forEach((r) => next.delete(r.id));
        return next;
      }
      const next = new Set(prev);
      deletableSorted.forEach((r) => next.add(r.id));
      return next;
    });
  }

  function clearSelection() {
    setSelected(new Set());
  }

  async function doBulkDelete() {
    const ids = [...liveSelection];
    // Fire all deletions in parallel; the engine endpoint is non-blocking
    // and react-query coalesces the list invalidations.
    await Promise.all(
      ids.map((id) =>
        del.mutateAsync(id).catch((e) => {
          console.warn(`failed to delete ${id}:`, e);
          return null;
        }),
      ),
    );
    setSelected(new Set());
    setConfirmOpen(false);
  }

  const selCount = liveSelection.size;

  return (
    <div className="p-6 space-y-5">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Runs</h2>
          <p className="text-sm text-muted-foreground">
            Active and historical pushes from scenarios to backends.
          </p>
        </div>
        {permissions.canMutate ? (
          <Button asChild>
            <Link to="/scenarios">Start a new run</Link>
          </Button>
        ) : (
          <Button disabled title={permissions.mutationReason ?? "Insufficient permission"}>
            Start a new run
          </Button>
        )}
      </header>

      {!permissions.canMutate && (
        <p className="text-xs text-muted-foreground">
          {permissions.mutationReason ?? "Insufficient permissions for this action."}
        </p>
      )}

      {selCount > 0 && (
        <div className="rounded-lg border border-primary/30 bg-primary/[0.05] px-3 py-2 flex items-center gap-3 sticky top-2 z-10 backdrop-blur">
          <span className="text-sm font-medium text-foreground">
            {selCount} run{selCount === 1 ? "" : "s"} selected
          </span>
          <div className="flex-1" />
          <Button
            variant="ghost"
            size="sm"
            onClick={clearSelection}
            className="h-8"
          >
            <X size={14} className="mr-1" /> Clear
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setConfirmOpen(true)}
            disabled={del.isPending || !permissions.canMutate}
            className="h-8"
          >
            <Trash2 size={14} className="mr-1" />
            {del.isPending ? "Deleting…" : `Delete ${selCount}`}
          </Button>
        </div>
      )}

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-input bg-background accent-primary cursor-pointer"
                  checked={allDeletableSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={toggleAll}
                  disabled={deletableSorted.length === 0 || !permissions.canMutate}
                  aria-label="Select all deletable runs"
                />
              </TableHead>
              <TableHead>ID</TableHead>
              <TableHead>Scenario</TableHead>
              <TableHead>Backend</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Phase</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-44 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((r) => {
              const checked = liveSelection.has(r.id);
              const deletable = isDeletable(r);
              return (
                <TableRow
                  key={r.id}
                  className={cn(
                    "cursor-pointer",
                    checked && "bg-primary/[0.04]",
                  )}
                  onClick={() => navigate(`/runs/${r.id}`)}
                >
                  <TableCell
                    className="w-10"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-input bg-background accent-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                      checked={checked}
                      disabled={!deletable || !permissions.canMutate}
                      onChange={() => toggleOne(r.id)}
                      title={
                        !permissions.canMutate
                          ? permissions.mutationReason ?? "Insufficient permission"
                          : deletable
                          ? "Select for bulk action"
                          : "Stop the run before deleting"
                      }
                      aria-label={`Select ${r.id}`}
                    />
                  </TableCell>
                  <TableCell className="font-mono text-xs text-foreground">{r.id}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {r.scenario_id}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    <BackendChips backends={r.backends} runStatus={r.status} />
                  </TableCell>
                  <TableCell>
                    <RunStatusBadge status={r.status} />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {r.phase ?? "—"}
                  </TableCell>
                  <TableCell
                    className="text-xs text-muted-foreground"
                    title={r.created_at}
                  >
                    {relativeTime(r.created_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <RunRowActions
                      run={r}
                      canMutate={permissions.canMutate}
                      mutationReason={permissions.mutationReason}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
            {sorted.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center text-muted-foreground py-12"
                >
                  No runs yet.{" "}
                  <Link to="/scenarios" className="underline text-foreground">
                    Pick a scenario
                  </Link>{" "}
                  to start one.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {selCount} run{selCount === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Removes the selected runs from history and deletes their
              generated bundles from disk. Telemetry already pushed to
              backends stays in the backends. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={del.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={del.isPending}
              onClick={(e) => {
                e.preventDefault();
                doBulkDelete();
              }}
            >
              {del.isPending ? "Deleting…" : `Delete ${selCount}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
